/**
 * Gemini free-tier RPM/RPD capacity gauge (2026-08-16, ao_open_work_consolidated_
 * tracker "build the rest now"). NOT a wallet/$ panel like DeepSeekWalletPanel --
 * Gemini's free tier has no $ concept at all, the real ceiling
 * is a raw request-rate quota (see the backend gemini_headroom.py module docstring).
 * Reuses the SAME real-time counting `gemini_account_has_rate_headroom` already
 * gates dispatch with -- this panel just reports the numbers instead of collapsing
 * them to a bool.
 *
 * TPM (2026-08-26, gemini_free_tier_tpm_unmeasured_retry_storm): the ceiling that
 * actually binds -- 250K INPUT tokens/min per project -- rendered from Google's own
 * token-family read (latest reported minute vs ceiling, peak minute in the recent
 * window, token-quota 429s in that window). Vendor-only: when the token read was
 * unavailable the row says so instead of showing a fabricated 0.
 */

import { useEffect, useState } from "react";

import { api } from "./api";
import { Panel } from "./components";
import type { GeminiAccountCapacityView } from "./types";

const POLL_INTERVAL_MS = 30000; // RPM ceilings are as tight as 5/min -- poll faster than the wallet panels' 60s.

/** Exported for AccountRow's GeminiCapacityLine (layout.tsx) — the same real
 * used-vs-ceiling formatting, reused rather than re-implemented, for the Gemini
 * accounts rendered inline in the generic Accounts panel. */
export function fmtCeiling(used: number, ceiling: number | null): string {
  return ceiling === null ? `${used} used (ceiling unknown — unregistered variant)` : `${used} / ${ceiling}`;
}

/** TPM row text (2026-08-26): the vendor's latest-minute granted input tokens against the
 * ceiling, plus the recent peak minute and token-quota 429s when the token read succeeded;
 * an honest "not available" otherwise. Exported so layout.tsx's inline account row and any
 * pure-mapper test render the identical string. */
export function fmtTpm(r: {
  tpm_ceiling: number | null;
  tpm_used: number | null;
  tpm_peak_recent: number | null;
  tpm_rejected_recent: number | null;
  tpm_source: string;
}): string {
  if (r.tpm_ceiling === null) return "unknown (unregistered variant)";
  if (r.tpm_source !== "vendor" || r.tpm_used === null) {
    return `${r.tpm_ceiling.toLocaleString()} ceiling — vendor token read unavailable`;
  }
  const peak = r.tpm_peak_recent === null ? "" : ` · peak 5m ${r.tpm_peak_recent.toLocaleString()}`;
  const rejected =
    r.tpm_rejected_recent === null ? "" : ` · ${r.tpm_rejected_recent.toLocaleString()} token-429s in 5m`;
  return `${r.tpm_used.toLocaleString()} / ${r.tpm_ceiling.toLocaleString()} (latest minute)${peak}${rejected}`;
}

function usagePct(used: number, ceiling: number | null): number | null {
  if (ceiling === null || ceiling <= 0) return null;
  return Math.min(100, (used / ceiling) * 100);
}

function GaugeBar({ used, ceiling }: { used: number; ceiling: number | null }) {
  const pct = usagePct(used, ceiling);
  if (pct === null) return null;
  const tone =
    pct >= 90 ? "capacity-gauge-fill-danger" : pct >= 60 ? "capacity-gauge-fill-warn" : "capacity-gauge-fill-ok";
  return (
    <div className="capacity-gauge-track">
      <div className={`capacity-gauge-fill ${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function GeminiCapacityPanel({ baseUrl }: { baseUrl: string }) {
  const [rows, setRows] = useState<GeminiAccountCapacityView[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await api.getGeminiCapacity(baseUrl);
        if (!cancelled) {
          setRows(result);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };
    void load();
    const id = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl]);

  return (
    <Panel title="Gemini Capacity" collapsible tone="accounts">
      <div className="usage-panel-hint">
        Gemini's free tier has no $ balance — the real ceilings are raw quotas. RPM/RPD show requests against the
        declared ceiling for that account's model variant; TPM (the one that binds — 250K input tokens/min per project)
        shows Google's own latest-minute granted tokens, recent peak and token-quota 429s from the Cloud Monitoring
        token family, with dispatch capped at one worker per project.
      </div>
      {error && <div className="task-usage-error">Failed to load: {error}</div>}
      {!error && !rows && <div className="task-usage-pending">loading…</div>}
      {!error && rows && rows.length === 0 && <div className="task-usage-pending">no Gemini accounts registered</div>}
      {!error && rows && rows.length > 0 && (
        <div className="gemini-capacity-accounts">
          {rows.map((r) => (
            <div key={r.account_id} className="gemini-capacity-account">
              <div className="capacity-account-header">
                <span className="mono">{r.label}</span>
                <span className="capacity-account-meta">
                  {r.variant ?? "unknown variant"}
                  {r.gcp_project ? ` · ${r.gcp_project}` : ""}
                </span>
              </div>
              <table className="acc-usage-table acc-usage-table-breakdown">
                <tbody>
                  <tr>
                    <td className="acc-usage-window-label">RPM</td>
                    <td className="mono">{fmtCeiling(r.rpm_used, r.rpm_ceiling)}</td>
                    <td>
                      <GaugeBar used={r.rpm_used} ceiling={r.rpm_ceiling} />
                    </td>
                  </tr>
                  <tr>
                    <td className="acc-usage-window-label">
                      RPD
                      <span
                        className="capacity-account-meta"
                        title={
                          r.rpd_source === "vendor"
                            ? "Real Cloud Monitoring read (Google's own count of granted requests)"
                            : "Local fallback: this fleet's own spawn-selection count — the vendor read was unavailable this tick, so this can undercount real usage"
                        }
                      >
                        {" "}
                        ({r.rpd_source === "vendor" ? "vendor" : "local"})
                      </span>
                    </td>
                    <td className="mono">{fmtCeiling(r.rpd_used, r.rpd_ceiling)}</td>
                    <td>
                      <GaugeBar used={r.rpd_used} ceiling={r.rpd_ceiling} />
                    </td>
                  </tr>
                  <tr>
                    <td className="acc-usage-window-label">
                      TPM
                      <span
                        className="capacity-account-meta"
                        title={
                          r.tpm_source === "vendor"
                            ? "Real Cloud Monitoring token-family read (Google's own granted input tokens per minute; lags real time by ingestion delay)"
                            : "The vendor token read was unavailable this tick — no local self-count exists for tokens, so nothing is shown rather than a fabricated 0"
                        }
                      >
                        {" "}
                        ({r.tpm_source === "vendor" ? "vendor" : "n/a"})
                      </span>
                    </td>
                    <td className="mono">{fmtTpm(r)}</td>
                    <td>
                      {r.tpm_source === "vendor" && r.tpm_used !== null && (
                        <GaugeBar used={r.tpm_used} ceiling={r.tpm_ceiling} />
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
